import React from "react";
import { Table } from "antd";
import { BookOutlined } from "@ant-design/icons";
import { Recipe, RecipeIngredient } from "../../../Types";

const columns = [
  {
    title: "Material",
    dataIndex: "raw_material_name",
    key: "raw_material_name",
    render: (v: string, item: RecipeIngredient) => (
      <div>
        <span className="font-medium text-gray-800 text-xs">{v}</span>
        {item.raw_material_batch_no && (
          <span className="ml-2 bg-blue-50 text-blue-600 text-[10px] px-1.5 py-0.5 rounded font-medium">
            Batch: {item.raw_material_batch_no}
          </span>
        )}
        <div className="text-[11px] text-gray-400">
          {item.raw_material_code}
        </div>
      </div>
    ),
  },
  {
    title: "Quantity (incl. wastage)",
    key: "quantity",
    render: (_: any, item: RecipeIngredient) => (
      <span className="text-xs text-gray-600">
        {item.quantity} {item.raw_material_unit} (+{item.wastage_quantity}{" "}
        wastage)
      </span>
    ),
  },
  {
    title: "Cost",
    dataIndex: "cost",
    key: "cost",
    render: (v: number) => (
      <span className="text-xs font-semibold text-gray-700">
        Rs. {v?.toFixed(2)}
      </span>
    ),
  },
];

interface Props {
  record: Recipe;
}

export const RecipeIngredientsView: React.FC<Props> = ({ record }) => (
  <div className="px-4 py-3 bg-gray-50 rounded-md border border-gray-100 my-1">
    <h4 className="text-[11px] font-semibold text-gray-400 mb-2 uppercase tracking-wider">
      Ingredients
    </h4>

    <Table
      dataSource={record.items || []}
      columns={columns}
      pagination={false}
      size="small"
      rowKey="id"
    />

    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white p-3 rounded-md border border-gray-100 text-xs">
      <div>
        <span className="text-gray-400 block uppercase text-[10px] tracking-wide">
          Yield / Batch Size
        </span>
        <span className="font-semibold text-gray-700">
          {record.batch_size || 1} units
        </span>
      </div>
      <div>
        <span className="text-gray-400 block uppercase text-[10px] tracking-wide">
          Additional Cost
        </span>
        <span className="font-semibold text-gray-700">
          Rs. {(record.additional_cost || 0).toFixed(2)}
        </span>
      </div>
      <div>
        <span className="text-gray-400 block uppercase text-[10px] tracking-wide">
          Total Recipe Cost
        </span>
        <span className="font-semibold text-gray-700">
          Rs. {(record.total_cost || 0).toFixed(2)}
        </span>
      </div>
      <div>
        <span className="text-indigo-600 block uppercase text-[10px] tracking-wide font-semibold">
          Unit Cost
        </span>
        <span className="font-bold text-indigo-700">
          Rs. {((record.total_cost || 0) / (record.batch_size || 1)).toFixed(2)}
        </span>
      </div>
    </div>

    {record.instructions && (
      <div className="mt-3 bg-white p-3 rounded-md border border-gray-100 text-xs text-gray-600">
        <div className="font-medium text-gray-500 mb-1 flex items-center gap-1.5">
          <BookOutlined className="text-gray-400" /> Instructions
        </div>
        <div className="whitespace-pre-wrap">{record.instructions}</div>
      </div>
    )}
  </div>
);
